import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { leftArrow, rightArrow } from "svg";
import { homeReviews as reviews } from "pages/Public/Reviews/reviews";

const DEFAULT_INTERVAL = 6500;

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      opacity: { duration: 0.28 },
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 500 : -500,
    opacity: 0,
    transition: {
      x: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      opacity: { duration: 0.2 },
    },
  }),
};

function ReviewDisplayer({ rotationMs = DEFAULT_INTERVAL }) {
  const [currentReview, setCurrentReview] = useState(0);
  const [direction, setDirection] = useState(0);
  // Bumped on tab-resume to force AnimatePresence remount, clearing any stuck
  // mid-flight slides that resulted from RAF being throttled while hidden.
  const [animKey, setAnimKey] = useState(0);
  const intervalRef = useRef(null);
  const pausedRef = useRef(false);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const paginate = useCallback((newDirection) => {
    setDirection(newDirection);
    setCurrentReview(
      (prev) => (prev + newDirection + reviews.length) % reviews.length
    );
  }, []);

  const jumpTo = useCallback((idx) => {
    setCurrentReview((prev) => {
      if (idx === prev) return prev;
      setDirection(idx > prev ? 1 : -1);
      return idx;
    });
  }, []);

  const restartInterval = useCallback(() => {
    clearTimer();
    if (pausedRef.current) return;
    intervalRef.current = setInterval(() => {
      paginate(1);
    }, rotationMs);
  }, [paginate, rotationMs]);

  useEffect(() => {
    restartInterval();
    return clearTimer;
  }, [currentReview, restartInterval]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        pausedRef.current = true;
        clearTimer();
      } else {
        pausedRef.current = false;
        setAnimKey((k) => k + 1);
        restartInterval();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [restartInterval]);

  const review = reviews[currentReview];

  return (
    <div className="review-box">
    <div className="review-stage">
      <div className="review-brick-wall" aria-hidden="true" />
      <div className="review-stage-inner">
        <div className="review-heading elegant-hero">
          Hear it from the locals
        </div>
        <div className="review-frame">
          <div className="review-frame-recess">
            <div className="review-frame-inner">
              <div className="review-frame-gold">
              <div className="review-mat">
            <button
              type="button"
              className="review-nav review-nav-prev"
              aria-label="Previous review"
              onClick={() => paginate(-1)}
            >
              {leftArrow()}
            </button>
            <div className="review-quote-wrapper">
              <AnimatePresence
                initial={false}
                custom={direction}
                mode="popLayout"
                key={animKey}
              >
                <motion.blockquote
                  key={currentReview}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="review-quote"
                >
                  <span className="review-quote-mark" aria-hidden="true">
                    &ldquo;
                  </span>
                  <span className="review-quote-text">
                    {review.text.replace(/^"|"$/g, "")}
                  </span>
                </motion.blockquote>
              </AnimatePresence>
            </div>
            <button
              type="button"
              className="review-nav review-nav-next"
              aria-label="Next review"
              onClick={() => paginate(1)}
            >
              {rightArrow()}
            </button>
          </div>
              </div>
            </div>
          </div>

          <div className="review-plaque" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.span
                key={currentReview}
                className="review-plaque-name"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {review.author}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
        <div
          className="review-dots"
          role="tablist"
          aria-label="Select review"
        >
          {reviews.map((_, idx) => (
            <button
              key={idx}
              type="button"
              role="tab"
              aria-selected={idx === currentReview}
              aria-label={`Review ${idx + 1} of ${reviews.length}`}
              className={`review-dot${
                idx === currentReview ? " is-active" : ""
              }`}
              onClick={() => jumpTo(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
  );
}

export default ReviewDisplayer;
